/**
 * Student Join Login Component
 *
 * The way in when ?mode=student-join is present in the URL: a student opening
 * a session that is running, from the portal, which builds the link. It joins
 * at once — its first paint is already the join's first step — with the
 * student's real Nakama custom ID and the session's externalId, which the
 * game server resolves to the session's running match: no match is created
 * here.
 *
 * The card says who is joining and how the join stands (JoinStatus): its
 * four steps, then the game takes over. A session whose match is not up yet
 * is waited for — asked again every few seconds for about a minute
 * (use-join-state.ts) — and any other refusal is said by its code
 * (domains/join-refusals.ts), with Retry where a second try can work. After
 * the menu's Disconnect it says the player left, and waits on Rejoin rather
 * than joining again at once (S1).
 *
 * The link (domains/entry-links.ts, readLink):
 *   mode=student-join     — activation flag
 *   user=<custom-id>      — student's real Nakama custom ID (Users.nakamaCustomId)
 *   name=<display>        — player first name
 *   cohort=<name>         — cohort name/slug the student is joining as
 *   world=<name>          — world name
 *   session=<externalId>  — WorldSessions.externalId; game-server resolves to match
 * The portal names the game too; the client is that game, and reads it not.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { readLink } from '../../domains/entry-links';
import { incompleteLink } from '../../domains/join-refusals';
import { useAuthStore } from '../../domains/auth';
import { authService } from '../../services/authentication/auth-service';
import { kickOffDocumentFullscreenFromGesture, shouldEnforceMatchFullscreen } from '../../utils/fullscreen';
import { EntryCard, EntryFacts, EntryWho, JoinStatus, RefusalNotice } from './entry-card';
import { EntryFrame } from './entry-frame';
import { useJoinState } from './use-join-state';

export function StudentJoinLogin({ search }: { search: string }) {
  const { t } = useTranslation();
  const link = useMemo(() => readLink(search, 'student-join'), [search]);

  /** Sends the join: the player's own, or one sent while waiting for the session to start. */
  const join = useCallback(
    (waiting: boolean) => {
      if (link.missing) return;
      const { user, world, cohort, session, name } = link.parts;
      void authService.connectFromStudentJoin(user, world, cohort, session, name, { waiting });
    },
    [link],
  );
  const askAgain = useCallback(() => join(true), [join]);
  const state = useJoinState(askAgain);

  // The portal opened the link: join at once — unless the player has just left the match.
  useEffect(() => {
    if (link.missing || useAuthStore.getState().leftMatch) return;
    if (shouldEnforceMatchFullscreen()) kickOffDocumentFullscreenFromGesture();
    join(false);
  }, [link, join]);

  const frame = { search, title: t('entry.join.title'), meaning: t('entry.join.subtitle') };

  // A link cut short: nothing here can mend it, and it names no one to show.
  if (link.missing) {
    return (
      <EntryFrame {...frame}>
        <EntryCard>
          <RefusalNotice words={incompleteLink(t('entry.refusal.reopenFromPortal'))} />
        </EntryCard>
      </EntryFrame>
    );
  }

  return (
    <EntryFrame {...frame} kicker={[link.parts.world]}>
      <EntryCard>
        <EntryWho label={t('entry.player')} name={link.parts.name} />
        <EntryFacts facts={[{ label: t('entry.fact.class'), value: link.parts.cohort }]} />
        <JoinStatus state={state} onJoin={() => join(false)} ask="admin" />
      </EntryCard>
    </EntryFrame>
  );
}
